import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { Fragment } from 'react';
import { AudioCardSkeleton } from './AudioCardSkeleton';
import { ConfirmDialog } from '../../Common/dialog';
import { Styles } from '@t2c/shared-fe';
import { AudioStatus, IAudioCreation } from '../../@types';
import { useAudioCreationManager } from '../../hooks';
import { RenameDialog } from './RenameDialog';
import EmptyPage from '../../Common/EmptyPage';
import { ContentType, EMPTY_PAGE_TEXT } from '../../utils/constant';
import { TextToSpeechCard } from './TextToSpeechCard';
import { Box, Button, CircularProgress, Hidden } from '@mui/material';
import { CardTypeEnum } from 'apps/t2c-fe/utils';
import { DeleteConfirmModal } from '../delete-confirm/DeleteConfirmModal';
import { InfiniteData } from '@tanstack/react-query';
import { GetAllCreatedAudiosResponse } from 'apps/t2c-fe/services/audio/getAllCreatedAudios';
import InfiniteScroll from 'react-infinite-scroll-component';

interface Props {
  isDataLoading: boolean;
  data: InfiniteData<GetAllCreatedAudiosResponse>;
  refetchTextToSpeechData: () => void;
  allAudios: boolean;
  fetchNextPage?: () => void;
  hasNextPage?: boolean;
  handleItemClick?: (index: number | null, url?: string, name?: string) => void;
  selectedIndex?: number | null;
  isTimelinePage?: boolean;
}

export const CreatedAudiosSection: React.FC<Props> = ({
  data,
  isDataLoading,
  refetchTextToSpeechData,
  allAudios,
  fetchNextPage,
  hasNextPage,
  handleItemClick,
  selectedIndex,
  isTimelinePage = false,
}) => {
  const {
    selectedAudio,
    isDeleteDialogOpen,
    isRenameDialogOpen,
    closeRenameDialog,
    handleOpenRenameDialog,
    handleDownload,
    handleOpenDeleteDialog,
    handleConfirmDelete,
    handleRename,
    closeDeleteDialog,
    isDeleteLoading,
    isEditNameLoading,
    handleViewAll,
    handlePreview,
  } = useAudioCreationManager({ refetchData: refetchTextToSpeechData });

  const menuList = [
    {
      id: 'preview',
      label: 'Preview',
      iconifyIcon: 'mdi:eye-outline',
      onClick: handlePreview,
    },
    {
      id: 'rename',
      label: 'Rename',
      onClick: handleOpenRenameDialog,
      iconifyIcon: 'mdi:rename',
    },
    {
      id: 'download',
      label: 'Download',
      onClick: handleDownload,
      iconifyIcon: 'material-symbols:download',
    },
    {
      id: 'delete',
      label: 'Delete',
      onClick: handleOpenDeleteDialog,
      iconifyIcon: 'material-symbols:delete',
    },
  ];

  return (
    <Stack rowGap={3} pb={2}>
      {/* {allAudios && (<Typography sx={style.title}>Your Created Audios</Typography>)} */}

      {!allAudios && (
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">Recent Creations</Typography>
          <Button onClick={handleViewAll}>View All</Button>
        </Box>
      )}

      {isDataLoading ? (
        <Stack sx={styles.loader}>
          <CircularProgress />
        </Stack>
      ) : !(data?.pages?.[0]?.data ?? []).length ? (
        <EmptyPage text={EMPTY_PAGE_TEXT.audio} type={ContentType.Audio} />
      ) : (
        <Fragment>
          {allAudios ? (
            <InfiniteScroll
              dataLength={data?.pages?.reduce((acc, page) => acc + page?.data?.length, 0) || 0}
              next={fetchNextPage}
              hasMore={hasNextPage}
              loader={
                <Box p={2} textAlign={'center'}>
                  <h2>loading...</h2>
                </Box>
              }
              scrollableTarget="scrollableDiv"
            >
              <Grid container spacing={3}>
                {data?.pages?.map((page, pageIndex) => (
                  <Fragment key={pageIndex}>
                    {page?.data?.map((item,index) => (
                      <Grid
                        key={item.id}
                        item
                        xs={12}
                        {...(!isTimelinePage && { sm: 6, md: 4, lg: 3 })}
                        onClick={() => {
                          if (item.status === AudioStatus.Success && handleItemClick)
                            handleItemClick(index, item.processedURL, item.name);
                        }}
                      >
                        <TextToSpeechCard data={item} menuActions={menuList} isActive={selectedIndex === index}/>
                      </Grid>
                    ))}
                  </Fragment>
                ))}
              </Grid>
            </InfiniteScroll>
          ) : (
            <Grid container spacing={3}>
              {data?.pages?.map((page, pageIndex) => (
                <Fragment key={pageIndex}>
                  {page?.data?.map((item) => (
                    <Grid key={item.id} item xs={12} sm={6} md={4} lg={3}>
                      <TextToSpeechCard data={item} menuActions={menuList} />
                    </Grid>
                  ))}
                </Fragment>
              ))}
            </Grid>
          )}
        </Fragment>
      )}
      <DeleteConfirmModal
        handleCancel={closeDeleteDialog}
        open={isDeleteDialogOpen}
        id={selectedAudio?.id}
        name={selectedAudio?.name}
        refetchData={refetchTextToSpeechData}
        type={CardTypeEnum.Voiceover}
      />
      <RenameDialog
        shouldOpenDialog={isRenameDialogOpen}
        handleCloseDialog={closeRenameDialog}
        initialName={selectedAudio?.name || ''}
        handleOnRename={handleRename}
        isLoading={isEditNameLoading}
        dialogTitle="Rename Audio"
      />
    </Stack>
  );
};

const styles: Styles = {
  title: {
    color: (theme) => theme.palette.text.primary,
    fontWeight: '500',
    fontSize: 24,
    mb: 2,
  },
  loader: {
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '20%',
  },
};
